import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { bs } from '../../../../styles/styles';
import { Button } from '../../../Button';
import { CustomDateRange } from '../../../CustomDateRange';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { DateRangeOption, IDayOption } from '../../models';
import { styleSheet } from './styles';

// Future date options - dates from today onwards
const futureDayOptions: IDayOption[] = (Object.keys(DateRangeOption) as (keyof typeof DateRangeOption)[]).map(key => {
	const value = DateRangeOption[key];
	if (value === DateRangeOption.Custom) {
		return {
			key,
			label: value,
			value,
		};
	}
	const parts = value.split(' ');
	return {
		key,
		label: value,
		value: moment()
			.add(parseInt(parts[0], 10), parts[1].toLowerCase() as moment.unitOfTime.DurationConstructor)
			.format('YYYY-MM-DD'),
	};
});

export const FutureRenewalDateFilterDropdown = ({
	selectedMinimumDate,
	setSelectedMinimumDate,
	selectedMaximumDate,
	setSelectedMaximumDate,
	anchorClassName,
}: {
	selectedMinimumDate: string;
	setSelectedMinimumDate: React.Dispatch<React.SetStateAction<string>>;
	selectedMaximumDate: string;
	setSelectedMaximumDate: React.Dispatch<React.SetStateAction<string | null>>;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [selectedDayOption, setSelectedDayOption] = React.useState<IDayOption | null>(null);

	const isCustomSelected = React.useMemo(() => {
		return selectedDayOption?.value === DateRangeOption.Custom;
	}, [selectedDayOption?.value]);

	React.useEffect(() => {
		if (selectedMinimumDate && selectedMaximumDate) {
			// Check if this matches a preset option (minimum = today, maximum = preset future date)
			const todayStr = moment().format('YYYY-MM-DD');
			if (selectedMinimumDate === todayStr) {
				const match = futureDayOptions.find((option: IDayOption) => {
					return option.value === selectedMaximumDate && option.value !== DateRangeOption.Custom;
				});
				if (match) {
					setSelectedDayOption(match);
					return;
				}
			}
			// Otherwise, it's a custom range
			const customDayOption = futureDayOptions.find(option => option.value === DateRangeOption.Custom);
			setSelectedDayOption(customDayOption);
			return;
		}
		setSelectedDayOption(null);
	}, [selectedMaximumDate, selectedMinimumDate]);

	const getDateRangeMessage = () => {
		if (!selectedMinimumDate || !selectedDayOption) {
			return 'Renewal Date';
		}

		if (selectedDayOption.value === DateRangeOption.Custom && selectedMinimumDate && selectedMaximumDate) {
			const selectedMinimumDateString = moment(selectedMinimumDate).format('MM/DD');
			const selectedMaximumDateString = moment(selectedMaximumDate).format('MM/DD');
			return `Renewal Date ${selectedMinimumDateString} - ${selectedMaximumDateString}`;
		}
		const key = selectedDayOption.key;
		return `Renewal Date/ ${DateRangeOption[key]}`;
	};

	const dropdownMessage = getDateRangeMessage();

	const filteredDayOptions = futureDayOptions.filter(option => {
		if (option.value === DateRangeOption.Custom && selectedDayOption?.value === DateRangeOption.Custom) {
			return false;
		}
		return true;
	});

	const selectDateRangeOption = (newDayOption: IDayOption, ev: React.MouseEvent<HTMLButtonElement>) => {
		if (newDayOption.value === DateRangeOption.Custom) {
			setSelectedDayOption(newDayOption);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			// For future dates, set both minimum (today) and maximum (future date)
			const todayStr = moment().format('YYYY-MM-DD');
			setSelectedMinimumDate(todayStr);
			setSelectedMaximumDate(newDayOption.value);
			setIsOpen(false);
		}
	};

	const resetFilter = () => {
		setSelectedMinimumDate(moment().format('YYYY-MM-DD'));
		setSelectedMaximumDate(null);
		setSelectedDayOption(null);
	};

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<div className={css(bs.truncateText)}>{dropdownMessage}</div>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu, styleSheet.dropdownMenuOverrides)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick={true}
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{isCustomSelected ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setSelectedMinimumDate(null);
								setSelectedMaximumDate(null);
							}}
							defaultOpen={!selectedMaximumDate}
							from={selectedMinimumDate ? moment(selectedMinimumDate).toDate() : today}
							to={selectedMaximumDate ? moment(selectedMaximumDate).toDate() : null}
							minDate={today}
							onChange={(from, to) => {
								setSelectedMinimumDate(moment(from).format('YYYY-MM-DD'));
								setSelectedMaximumDate(to ? moment(to).format('YYYY-MM-DD') : moment(from).format('YYYY-MM-DD'));
							}}
							showTextInput
							disableScrollTo
						/>
					) : null}
				</div>

				<div>
					{!isCustomSelected &&
						filteredDayOptions.map(dayOption => {
							return (
								<Button
									label={dayOption.label}
									key={`future-renewal-date-range-option-${dayOption.key}`}
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => selectDateRangeOption(dayOption, e)}
									kind='custom'
									className={css(
										dropdownStyles.option,
										selectedDayOption?.key === dayOption.key ? dropdownStyles.selected : null
									)}
								/>
							);
						})}
				</div>

				<Button label='Reset Filter' kind='custom' className={css(bs.brandLink)} type='button' onClick={resetFilter} />
			</div>
		</Dropdown>
	);
};
